import * as z from "zod/v4";
import { generateMockFromSchema } from "./generate-mock-from-schema";
import { getMockRestResponse, isMockModeEnabled } from "./mock-storage";

export interface MockRestEndpoint {
  /** URL path suffix to match against (e.g. "/team", "/user", "/onboarding") */
  pattern: string;
  /** HTTP method to match. Defaults to "GET" */
  method?: string;
  /** Zod schema for generating mock response (used if data is not provided) */
  schema?: z.ZodTypeAny;
  /** Static data to return. Takes precedence over schema. */
  data?: unknown;
}

const registry: MockRestEndpoint[] = [];
let interceptorInstalled = false;

export function registerMockRestEndpoint(endpoint: MockRestEndpoint): void {
  registry.push({ method: "GET", ...endpoint });
}

export function registerMockRestEndpoints(endpoints: MockRestEndpoint[]): void {
  for (const endpoint of endpoints) {
    registerMockRestEndpoint(endpoint);
  }
}

function findMockEndpoint(
  url: string,
  method: string,
): MockRestEndpoint | undefined {
  const upperMethod = method.toUpperCase();
  // Strip query params for matching
  const pathname = url.includes("?") ? url.slice(0, url.indexOf("?")) : url;
  return registry.find((entry) => {
    if (entry.method?.toUpperCase() !== upperMethod) {
      return false;
    }
    // Match if the pathname ends with the pattern (handles base URL prefixes)
    return (
      pathname.endsWith(entry.pattern) || pathname.endsWith(entry.pattern + "/")
    );
  });
}

export function installMockRestInterceptor(): void {
  if (interceptorInstalled || typeof window === "undefined") {
    return;
  }
  interceptorInstalled = true;

  const OriginalXHR = window.XMLHttpRequest;
  const originalOpen = OriginalXHR.prototype.open;
  const originalSend = OriginalXHR.prototype.send;

  OriginalXHR.prototype.open = function (
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null,
  ) {
    (this as any).__mockMethod = method;
    (this as any).__mockUrl = String(url);
    return originalOpen.call(
      this,
      method,
      url,
      async ?? true,
      username,
      password,
    );
  };

  OriginalXHR.prototype.send = function (
    body?: Document | XMLHttpRequestBodyInit | null,
  ) {
    if (!isMockModeEnabled()) {
      return originalSend.call(this, body);
    }

    const url: string = (this as any).__mockUrl ?? "";
    const method: string = (this as any).__mockMethod ?? "GET";
    const match = findMockEndpoint(url, method);

    if (!match) {
      return originalSend.call(this, body);
    }

    const mockData = resolveMockRestData(match);
    const responseText = JSON.stringify(mockData);

    console.log(`[redoMock] REST intercepted: ${method} ${url} → mock data`);

    // Simulate async response
    setTimeout(() => {
      Object.defineProperty(this, "readyState", { writable: true, value: 4 });
      Object.defineProperty(this, "status", { writable: true, value: 200 });
      Object.defineProperty(this, "statusText", {
        writable: true,
        value: "OK",
      });
      Object.defineProperty(this, "responseText", {
        writable: true,
        value: responseText,
      });
      Object.defineProperty(this, "response", {
        writable: true,
        value: responseText,
      });
      this.dispatchEvent(new Event("readystatechange"));
      this.dispatchEvent(new Event("load"));
      this.dispatchEvent(new Event("loadend"));
    }, 0);
  };
}

export function getMockRestRegistry(): readonly MockRestEndpoint[] {
  return registry;
}

function resolveMockRestData(endpoint: MockRestEndpoint): unknown {
  const override = getMockRestResponse(endpoint.pattern);
  if (override !== undefined) {
    return override;
  }
  if (endpoint.data !== undefined) {
    return endpoint.data;
  }
  if (endpoint.schema) {
    return generateMockFromSchema(endpoint.schema, `rest:${endpoint.pattern}`);
  }
  return {};
}

export async function resolveMockForRestEndpoint(
  pattern: string,
): Promise<unknown> {
  const endpoint = registry.find((e) => e.pattern === pattern);
  if (!endpoint) {
    return {};
  }
  return resolveMockRestData(endpoint);
}
